import { FC, useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, Navigate, useNavigate } from 'react-router';
import { Title } from '../../components/Title';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Container,
    Divider,
    CardHeader,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    useMediaQuery,
    Theme,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Snackbar,
    Alert,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import type { CheckoutInfoResponse } from '../../types/checkout';
import { accountService, UserRole } from '../../account.service';
import { clientService } from '../../client.service';
import { cartService } from '../../cart.service';
import type { CheckoutCreateRequest } from '../../cart.service';
import { useMounted } from '../../hooks/use-mounted';
import { Scrollbar } from '../../components/Scrollbar';
import { PropertyList } from '../../components/PropertyList';
import { PropertyListItem } from '../../components/PropertyListItem';
import numeral from 'numeral';
import CreditCard from 'react-credit-cards';
import { formatCreditCardNumber, formatExpirationDate, formatCVC } from '../../utils/credit-cards';
import 'react-credit-cards/es/styles-compiled.css';
import creditCards from '../../images/credit-cards.png';
import type { CheckoutRequest } from '../../types/checkout';
import { IToastState, defaultToastState } from '../../components/ToastState';
import Message from '../Message';
import { settings } from '../../settings';
import { CountryNameToCode, shortProvinceOrStateName } from '../../utils/cart';
import { LIFETIME_SUPPORT_YEARS, ANNUAL_SUPPORT_ID, paypalClientId } from '../../AppSettings';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const translateCardType = (issuer: string): string => {
    switch (issuer) {
        case 'visa':
            return 'VISA';
        case 'mastercard':
            return 'MC';
        case 'amex':
            return 'AMEX';
        case 'dinersclub':
            return 'DINE';
        case 'discover':
            return 'DISC';
        default:
            return '';
    }
};

interface CheckoutProps {
    isPublic: boolean;
}

export const Checkout: FC<CheckoutProps> = ({ isPublic }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const account = accountService.getAccount();
    const { pid, qty, lic, order, pid_from } = location.state || {};
    const isUpgrade = pid_from > 0;

    // Public buyers reach this page through order-lookup links without a JWT,
    // so they must use the anonymous /api/cart/* endpoints. Signed-in clients
    // keep the auth-gated /api/clients/* endpoints.
    const checkoutSvc = isPublic ? cartService : clientService;

    const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const align = smDown ? 'vertical' : 'horizontal';

    const isMounted = useMounted();
    const [info, setInfo] = useState<CheckoutInfoResponse | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [openBlacklistedDialog, setOpenBlacklistedDialog] = useState(false);
    const [openMonerisDialog, setOpenMonerisDialog] = useState(false);
    const [dialogTitle, setDialogTitle] = useState('');
    const [dialogMessage, setDialogMessage] = useState('');
    const [dialogFollowLink, setDialogFollowLink] = useState('');
    // Set when the API blocks a create-order as a recent duplicate (409). Drives
    // the "View your order" link in the failure dialog (-> /invoices/{id}).
    const [duplicateOrderId, setDuplicateOrderId] = useState('');
    const [toastState, setToastState] = useState<IToastState>(defaultToastState);
    const [loadError, setLoadError] = useState<string>(undefined);

    const [cardNumber, setCardNumber] = useState<string>('');
    const [expiry, setExpiry] = useState<string>('');
    const [cvc, setCvc] = useState<string>('');
    const [focused, setFocused] = useState<string>('');
    const [issuer, setIssuer] = useState<string>('');
    const [inProgress, setInProgress] = useState<boolean>(false);
    const { handleSubmit } = useForm<FormData>();

    const inProgressRef = useRef(false);

    // PayPal SDK flow: uses clientService.createPayPalOrder /
    // capturePayPalOrder via the authenticated /clients/paypal/* endpoints.
    const [paypalCheckoutToken, setPaypalCheckoutToken] = useState<string>('');
    const paypalOrderRef = useRef<{ token: string; orderId: string } | null>(null);
    // Carries the server's real create-order failure reason from
    // handlePayPalCreateOrder to the SDK onError callback (which fires after the
    // createOrder promise rejects). Null for SDK-internal errors → generic text.
    const paypalErrorRef = useRef<string | null>(null);
    // Carries the existing OrderID from a duplicate-order 409 raised in
    // handlePayPalCreateOrder out to the PayPal onError handler (which runs
    // after the SDK rejects), so it can offer the "View your order" link too.
    const paypalDuplicateOrderRef = useRef<string | null>(null);

    const loadData = useCallback(async () => {
        try {
            const data = await clientService.getCheckoutInfo(pid, qty, lic, order, isUpgrade, pid_from);
            if (isMounted()) {
                if (data.ok) {
                    setInfo(data.body);
                } else {
                    setLoadError(data.message);
                }
            }
        } catch (err) {
            console.error(err);
        }
    }, [isMounted]);

    useEffect(() => {
        loadData();
    }, []);

    const handleCallback = ({ issuer }, isValid) => {
        if (isValid) {
            setIssuer(issuer);
        } else {
            setIssuer('');
        }
    };

    const handleInputFocus = ({ target }) => {
        setFocused(target.name);
    };

    const handleInputChange = ({ target }) => {
        if (target.name === 'number') {
            target.value = formatCreditCardNumber(target.value);
            setCardNumber(target.value);
        } else if (target.name === 'expiry') {
            target.value = formatExpirationDate(expiry, target.value);
            setExpiry(target.value);
        } else if (target.name === 'cvc') {
            target.value = formatCVC(target.value, cardNumber);
            setCvc(target.value);
        }
    };

    const onSubmit = async (data) => {
        if (inProgressRef.current) return;
        if (!info) {
            // Checkout info never loaded (e.g. upgrade already consumed,
            // session expired, or direct navigation without state). Surface
            // it instead of letting null deref throw.
            setDialogTitle('Cannot Submit');
            setDialogMessage(
                loadError ||
                    'Checkout information is not loaded. Please refresh the page or return to your upgrades list.'
            );
            setDialogFollowLink('/client/upgrades');
            setOpenDialog(true);
            return;
        }
        inProgressRef.current = true;
        setInProgress(true);

        try {
            // Create server-side checkout session with validated prices
            const country = CountryNameToCode(info.country);
            const state = country === 'CA' || country === 'US' ? shortProvinceOrStateName(info.state) : info.state;
            const checkoutRequest: CheckoutCreateRequest = {
                type: translateCardType(issuer),
                // existingOrderId means "I'm paying an outstanding PORD/QUOT/INV";
                // PayPalCapture branches on it and UPDATEs that order instead
                // of inserting a new one. The profile-side Checkout page only
                // ever creates new orders (upgrades, annual-support renewals);
                // it never completes existing PORD/QUOT/INV, so suppress the
                // field. Source/original order context is conveyed by
                // upgradeFromOrderId + upgradeToken (upgrade) or by Comments
                // (annual support).
                existingOrderId: undefined,
                upgradeFromOrderId: isUpgrade ? info.orderId : undefined,
                upgradeToken: isUpgrade ? `${order}-${pid}-${lic}-${qty}-${pid_from}` : undefined,
                clientId: isPublic ? undefined : info.clientId,
                // For upgrade orders, store the source order ID in DiscountCode.
                // This is the back-reference that spWebClientUpgrades reads to
                // exclude already-used orders from future upgrade lists.
                // The server uses upgradeToken (not DiscountCode) for the
                // actual discount math, so this is safe.
                discountCode: isUpgrade ? info.orderId : '',
                itemsJson: JSON.stringify([
                    {
                        pid: info.productId,
                        lic: info.licenseId,
                        qty: info.quantity,
                    },
                ]),
                billingJson: JSON.stringify({
                    firstName: info.firstName,
                    lastName: info.lastName,
                    company: info.company,
                    address: info.address,
                    city: info.city,
                    state: state,
                    country: country,
                    zip: info.zip,
                    email: info.email,
                    phone: info.phone,
                }),
                comments: info.productId === ANNUAL_SUPPORT_ID ? info.orderId : '',
            };
            const checkoutResult = await checkoutSvc.createCheckout(checkoutRequest);
            if (!checkoutResult.ok || !checkoutResult.body) {
                // A 401 here is an expired/missing session, not a card failure.
                const isAuthError = checkoutResult.error?.status === 'unauthorized';
                setDialogTitle(isAuthError ? 'Session Expired' : 'Transaction Failed');
                setDialogMessage(
                    isAuthError
                        ? 'Your session has expired. Please sign in and try again.'
                        : checkoutResult.message || 'Failed to create checkout session'
                );
                setDialogFollowLink('');
                // Duplicate-order 409: offer a link to the order already placed.
                setDuplicateOrderId(
                    checkoutResult.error?.status === 'duplicate_recent_order'
                        ? checkoutResult.error.existingOrderId ?? ''
                        : ''
                );
                setOpenDialog(true);
                setInProgress(false);
                return;
            }
            const checkoutSession = checkoutResult.body;

            const request: CheckoutRequest = {
                checkout_token: checkoutSession.token,
                client_id: info.clientId,
                credit_card_brand: translateCardType(issuer),
                credit_card_number: cardNumber.replaceAll(' ', ''),
                expire_month: expiry.substr(0, 2),
                expire_year: '20' + expiry.substr(3, 2),
                cvv: cvc,
                product_id: info.productId,
                product: info.productName,
                license: info.licenseId,
                quantity: info.quantity,
            };

            const response = await clientService.checkout(request);

            if (response == null) {
                setDialogTitle('Transaction Failed');
                setDialogMessage('Server communication error');
                setDialogFollowLink('');
                setOpenDialog(true);
                setInProgress(false);
            } else if (!response.status) {
                if (response.message === 'Blacklisted') {
                    setOpenBlacklistedDialog(true);
                    setInProgress(false);
                } else if (response.message === 'moneris problem') {
                    setOpenMonerisDialog(true);
                    setInProgress(false);
                } else {
                    setDialogTitle('Transaction Failed');
                    setDialogMessage(response.message);
                    setDialogFollowLink('');
                    setOpenDialog(true);
                    setInProgress(false);
                }
            } else {
                setIssuer('');
                setCardNumber('');
                setExpiry('');
                setCvc('');
                setInProgress(false);
                navigate(`/invoices/${response.order}`);
            }
        } finally {
            inProgressRef.current = false;
        }
    };

    const handlePayPalCreateOrder = async (): Promise<string> => {
        if (!info) {
            throw new Error('Checkout information is not loaded');
        }
        // Guard against the SDK calling createOrder twice for the same click.
        if (paypalOrderRef.current) {
            return paypalOrderRef.current.orderId;
        }

        const country = CountryNameToCode(info.country);
        const state = country === 'CA' || country === 'US' ? shortProvinceOrStateName(info.state) : info.state;
        const checkoutRequest: CheckoutCreateRequest = {
            type: 'PPAL',
            // See onSubmit comment: existingOrderId would tell PayPalCapture
            // to UPDATE that existing order in place. Profile-side Checkout
            // always creates new orders, so omit the field.
            existingOrderId: undefined,
            upgradeFromOrderId: isUpgrade ? info.orderId : undefined,
            upgradeToken: isUpgrade ? `${order}-${pid}-${lic}-${qty}-${pid_from}` : undefined,
            clientId: isPublic ? undefined : info.clientId,
            discountCode: isUpgrade ? info.orderId : '',
            itemsJson: JSON.stringify([
                {
                    pid: info.productId,
                    lic: info.licenseId,
                    qty: info.quantity,
                },
            ]),
            billingJson: JSON.stringify({
                firstName: info.firstName,
                lastName: info.lastName,
                company: info.company,
                address: info.address,
                city: info.city,
                state: state,
                country: country,
                zip: info.zip,
                email: info.email,
                phone: info.phone,
            }),
            comments: info.productId === ANNUAL_SUPPORT_ID ? info.orderId : '',
        };

        const result = await checkoutSvc.createPayPalOrder(checkoutRequest);
        if (!result.ok || !result.body) {
            // Stash the server's reason so onError can show it; the SDK fires
            // onError with its own wrapped error once this promise rejects.
            paypalErrorRef.current = result.message || 'Failed to create PayPal order';
            // Duplicate-order 409: stash the existing OrderID so onError can
            // point the buyer at the order they already placed.
            paypalDuplicateOrderRef.current =
                result.error?.status === 'duplicate_recent_order' ? result.error.existingOrderId ?? null : null;
            throw new Error(paypalErrorRef.current);
        }
        paypalOrderRef.current = { token: result.body.checkoutToken, orderId: result.body.payPalOrderId };
        setPaypalCheckoutToken(result.body.checkoutToken);
        return result.body.payPalOrderId;
    };

    const handlePayPalApprove = async (data: { orderID: string }): Promise<void> => {
        setOpenBlacklistedDialog(false);
        setOpenMonerisDialog(false);
        paypalOrderRef.current = null;

        const result = await checkoutSvc.capturePayPalOrder(data.orderID, paypalCheckoutToken);
        if (result.ok && result.body?.success && result.body.orderId) {
            navigate(`/invoices/${result.body.orderId}`);
        } else {
            setDialogTitle('Transaction Failed');
            setDialogMessage(result.body?.message || result.message || 'Payment capture failed');
            setDialogFollowLink('');
            setOpenDialog(true);
        }
    };

    // Shared tail of every PayPal onError handler: surface the stashed server
    // reason (or fallback) in the failure dialog, expose a "View your order"
    // link if the failure was a duplicate-order 409, then clear the refs.
    const handlePayPalError = () => {
        paypalOrderRef.current = null;
        setDialogTitle('PayPal Error');
        setDialogMessage(paypalErrorRef.current || 'Payment could not be processed. Please try again.');
        setDuplicateOrderId(paypalDuplicateOrderRef.current || '');
        paypalErrorRef.current = null;
        paypalDuplicateOrderRef.current = null;
        setDialogFollowLink('');
        setOpenDialog(true);
    };

    const onDialogOk = () => {
        setOpenDialog(false);
        setDuplicateOrderId('');
        if (dialogFollowLink.length > 0) {
            window.open(dialogFollowLink, '_blank');
        }
    };

    const handleCloseBlacklistedDialog = () => {};
    const handleCancelBlacklistedDialog = () => {
        setOpenBlacklistedDialog(false);
    };

    const handleCloseMonerisDialog = () => {};
    const handleCancelMonerisDialog = () => {
        setOpenMonerisDialog(false);
    };

    const handleCloseSnackbar = (event?: React.SyntheticEvent, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setToastState({ ...toastState, open: false });
    };

    if (loadError) {
        return <Message title="Invalid Request" message={loadError} />;
    }

    return (
        <>
            {!isPublic && (!account || account.role !== UserRole.Client) && <Navigate to="/account/login" />}
            <Title title="Checkout" />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    py: 3,
                }}
            >
                <Container maxWidth="lg">
                    <Dialog
                        open={openDialog}
                        onClose={() => {
                            setOpenDialog(false);
                            setDuplicateOrderId('');
                        }}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">{dialogTitle}</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">{dialogMessage}</DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            {duplicateOrderId && (
                                <Button
                                    onClick={() => {
                                        setOpenDialog(false);
                                        setDuplicateOrderId('');
                                        navigate(`/invoices/${duplicateOrderId}`);
                                    }}
                                >
                                    View your order
                                </Button>
                            )}
                            <Button onClick={() => onDialogOk()} autoFocus>
                                OK
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={openBlacklistedDialog}
                        onClose={handleCloseBlacklistedDialog}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">Payment pre-processing validation failed</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                To process payment with Credit or Debit Card you will be <b>redirected to PayPal</b>{' '}
                                where you can select option to pay with Credit/Debit Card as a guest. After successful
                                payment you will be redirected back here to get an invoice and registration key.
                            </DialogContentText>
                            <DialogContentText sx={{ mt: 2 }}>
                                You can also contact our sales department {settings.phone1} for order processing over
                                the phone.
                            </DialogContentText>
                            <Box sx={{ mt: 2 }}>
                                <PayPalScriptProvider
                                    options={{ clientId: paypalClientId, currency: 'USD', disableFunding: 'paylater' }}
                                >
                                    <PayPalButtons
                                        style={{
                                            layout: 'vertical',
                                            color: 'gold',
                                            shape: 'rect',
                                            label: 'paypal',
                                        }}
                                        createOrder={handlePayPalCreateOrder}
                                        onApprove={handlePayPalApprove}
                                        onError={() => {
                                            setOpenBlacklistedDialog(false);
                                            handlePayPalError();
                                        }}
                                    />
                                </PayPalScriptProvider>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button sx={{ mr: 2 }} onClick={handleCancelBlacklistedDialog} autoFocus>
                                Cancel
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={openMonerisDialog}
                        onClose={handleCloseMonerisDialog}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">Payment processing failed</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                We experience payment processing issues with our provider. You can try again to process
                                payment later on, or use PayPal as a credit card payment processor.
                            </DialogContentText>
                            <DialogContentText sx={{ mt: 2 }}>
                                To process payment with Credit or Debit Card you will be <b>redirected to PayPal</b>{' '}
                                where you can select option to pay with Credit/Debit Card as a guest. After successful
                                payment you will be redirected back here to get an invoice and registration key.
                            </DialogContentText>
                            <DialogContentText sx={{ mt: 2 }}>
                                You can also contact our sales department {settings.phone1} for order processing over
                                the phone.
                            </DialogContentText>
                            <Box sx={{ mt: 2 }}>
                                <PayPalScriptProvider
                                    options={{ clientId: paypalClientId, currency: 'USD', disableFunding: 'paylater' }}
                                >
                                    <PayPalButtons
                                        style={{
                                            layout: 'vertical',
                                            color: 'gold',
                                            shape: 'rect',
                                            label: 'paypal',
                                        }}
                                        createOrder={handlePayPalCreateOrder}
                                        onApprove={handlePayPalApprove}
                                        onError={() => {
                                            setOpenMonerisDialog(false);
                                            handlePayPalError();
                                        }}
                                    />
                                </PayPalScriptProvider>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button sx={{ mr: 2 }} onClick={handleCancelMonerisDialog} autoFocus>
                                Cancel
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Snackbar
                        open={toastState.open}
                        autoHideDuration={6000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    >
                        <Alert onClose={handleCloseSnackbar} severity={toastState.status}>
                            {toastState.message}
                        </Alert>
                    </Snackbar>

                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Card>
                                <CardHeader title="Order Summary" />
                                <CardContent sx={{ py: 0 }}>
                                    <Scrollbar>
                                        <Box sx={{ minWidth: 700 }}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Product</TableCell>
                                                        <TableCell>License</TableCell>
                                                        <TableCell align="right">Price</TableCell>
                                                        <TableCell align="right">Quantity</TableCell>
                                                        <TableCell align="right">Amount</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    <TableRow>
                                                        <TableCell>{info && info.productName}</TableCell>
                                                        <TableCell>{info && info.licenseName}</TableCell>
                                                        <TableCell align="right">
                                                            {info && `$${numeral(info.price).format('0,0.00')}`}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {info &&
                                                                (info.productId === ANNUAL_SUPPORT_ID &&
                                                                info.quantity === LIFETIME_SUPPORT_YEARS
                                                                    ? 'Lifetime'
                                                                    : info.quantity)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {info && `$${numeral(info.subtotal).format('0,0.00')}`}
                                                        </TableCell>
                                                    </TableRow>

                                                    {info && info.discount > 0 && (
                                                        <>
                                                            <TableRow>
                                                                <TableCell />
                                                                <TableCell />
                                                                <TableCell />
                                                                <TableCell align="right">Discount</TableCell>
                                                                <TableCell align="right">
                                                                    {info &&
                                                                        `-$${numeral(info.discount).format('0,0.00')}`}
                                                                </TableCell>
                                                            </TableRow>

                                                            <TableRow>
                                                                <TableCell />
                                                                <TableCell />
                                                                <TableCell />
                                                                <TableCell align="right">Subtotal</TableCell>
                                                                <TableCell align="right">
                                                                    {info &&
                                                                        `$${numeral(info.subtotalAfterDiscount).format(
                                                                            '0,0.00'
                                                                        )}`}
                                                                </TableCell>
                                                            </TableRow>
                                                        </>
                                                    )}

                                                    {info &&
                                                        info.taxes &&
                                                        info.taxes.map((tax, index) => {
                                                            return (
                                                                <TableRow key={index}>
                                                                    <TableCell />
                                                                    <TableCell />
                                                                    <TableCell />
                                                                    <TableCell align="right">{tax.name}</TableCell>
                                                                    <TableCell align="right">
                                                                        {`$${numeral(tax.amount).format('0,0.00')}`}
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}

                                                    {info && (
                                                        <TableRow>
                                                            <TableCell />
                                                            <TableCell />
                                                            <TableCell />
                                                            <TableCell align="right">
                                                                <Typography style={{ fontWeight: 600 }}>
                                                                    Order Total
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography style={{ fontWeight: 600 }}>
                                                                    {`US $${numeral(info.total).format('0,0.00')}`}{' '}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    </Scrollbar>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <Box sx={{ height: '100%' }}>
                                <Card sx={{ height: '100%' }}>
                                    <CardHeader title="Billing information" />
                                    <Divider />
                                    <Box sx={{ px: 3 }}>
                                        <PropertyList>
                                            <PropertyListItem align={align} label="Name">
                                                <Typography color="primary" variant="body1">
                                                    {info && `${info.firstName} ${info.lastName}`}
                                                </Typography>
                                            </PropertyListItem>
                                            <Divider />
                                            <Box>
                                                <PropertyListItem align={align} label="Company">
                                                    <Typography variant="body1">{info && info.company}</Typography>
                                                </PropertyListItem>
                                            </Box>
                                            <Divider />
                                            <Box>
                                                <PropertyListItem align={align} label="Address">
                                                    <Typography variant="body1">{info && info.address}</Typography>
                                                </PropertyListItem>
                                            </Box>
                                            <Divider />
                                            <Box>
                                                <PropertyListItem align={align} label="City">
                                                    <Typography variant="body1">{info && info.city}</Typography>
                                                </PropertyListItem>
                                            </Box>
                                            <Divider />
                                            <Box>
                                                <PropertyListItem align={align} label="State / Province">
                                                    <Typography variant="body1">{info && info.state}</Typography>
                                                </PropertyListItem>
                                            </Box>
                                            <Divider />
                                            <Box>
                                                <PropertyListItem align={align} label="Zip / Postal code">
                                                    <Typography variant="body1">{info && info.zip}</Typography>
                                                </PropertyListItem>
                                            </Box>
                                            <Divider />
                                            <Box>
                                                <PropertyListItem align={align} label="Country">
                                                    <Typography variant="body1">{info && info.country}</Typography>
                                                </PropertyListItem>
                                            </Box>
                                            <Divider />
                                            <Box>
                                                <PropertyListItem align={align} label="Phone">
                                                    <Typography variant="body1">{info && info.phone}</Typography>
                                                </PropertyListItem>
                                            </Box>
                                            <Divider />
                                            <Box>
                                                <PropertyListItem align={align} label="Fax">
                                                    <Typography variant="body1">{info && info.fax}</Typography>
                                                </PropertyListItem>
                                            </Box>
                                            <Divider />
                                            <Box>
                                                <PropertyListItem align={align} label="Email">
                                                    <Typography variant="body1" color="primary">
                                                        {info && info.email}
                                                    </Typography>
                                                </PropertyListItem>
                                            </Box>
                                            <Divider />
                                            <Box>
                                                <PropertyListItem align={align} label="Original Order ID">
                                                    <Typography variant="body1" color="primary">
                                                        {info && info.orderId}
                                                    </Typography>
                                                </PropertyListItem>
                                            </Box>
                                            <Divider />
                                        </PropertyList>
                                    </Box>
                                </Card>
                            </Box>
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <Box sx={{ height: '100%' }}>
                                <Card sx={{ height: '100%' }}>
                                    <CardContent sx={{ px: 3 }}>
                                        <Box
                                            sx={{
                                                mt: 0,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                    <Typography variant="h6">Pay with &nbsp;</Typography>
                                                    <img alt="credit cards" style={{ width: 340 }} src={creditCards} />
                                                </Box>

                                                <CreditCard
                                                    acceptedCards={[
                                                        'visa',
                                                        'mastercard',
                                                        'amex',
                                                        'dinersclub',
                                                        'discover',
                                                    ]}
                                                    number={cardNumber}
                                                    name=""
                                                    expiry={expiry}
                                                    cvc={cvc}
                                                    focused={focused}
                                                    callback={handleCallback}
                                                />
                                            </Box>
                                            <Box>
                                                <Grid container columnSpacing={3}>
                                                    <Grid item xs={12}>
                                                        <TextField
                                                            name="number"
                                                            value={cardNumber}
                                                            required
                                                            label="Card Number"
                                                            fullWidth
                                                            onChange={handleInputChange}
                                                            onFocus={handleInputFocus}
                                                            sx={{
                                                                flexGrow: 1,
                                                                mr: 3,
                                                                mt: 3,
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item md={6} xs={12}>
                                                        <TextField
                                                            name="expiry"
                                                            value={expiry}
                                                            required
                                                            label="Valid Thru (MM/YY)"
                                                            fullWidth
                                                            onChange={handleInputChange}
                                                            onFocus={handleInputFocus}
                                                            sx={{
                                                                flexGrow: 1,
                                                                mr: 3,
                                                                mt: 3,
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item md={6} xs={12}>
                                                        <TextField
                                                            name="cvc"
                                                            value={cvc}
                                                            label="CVC"
                                                            fullWidth
                                                            onChange={handleInputChange}
                                                            onFocus={handleInputFocus}
                                                            sx={{
                                                                flexGrow: 1,
                                                                mr: 3,
                                                                mt: 3,
                                                            }}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={12}>
                                                        <form id="payment" onSubmit={handleSubmit(onSubmit)}>
                                                            <Button
                                                                color="primary"
                                                                variant="contained"
                                                                type="submit"
                                                                fullWidth
                                                                disabled={!issuer || expiry.length !== 5 || inProgress}
                                                                sx={{
                                                                    mt: 3,
                                                                    height: 48,
                                                                    fontSize: '1rem',
                                                                    fontWeight: 700,
                                                                    textTransform: 'none',
                                                                }}
                                                            >
                                                                PAY with Card
                                                            </Button>
                                                        </form>
                                                        <Divider sx={{ m: 3 }} variant="middle">
                                                            OR
                                                        </Divider>

                                                        <Box sx={{ mt: 2 }}>
                                                            <PayPalScriptProvider
                                                                options={{
                                                                    clientId: paypalClientId,
                                                                    currency: 'USD',
                                                                    disableFunding: 'paylater',
                                                                }}
                                                            >
                                                                <PayPalButtons
                                                                    style={{
                                                                        layout: 'vertical',
                                                                        color: 'gold',
                                                                        shape: 'rect',
                                                                        label: 'paypal',
                                                                        height: 48,
                                                                    }}
                                                                    createOrder={handlePayPalCreateOrder}
                                                                    onApprove={handlePayPalApprove}
                                                                    onError={() => {
                                                                        handlePayPalError();
                                                                    }}
                                                                />
                                                            </PayPalScriptProvider>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sx={{}}>
                            <Divider />
                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                                <Typography variant="subtitle2">
                                    Secure payment services provided by{' '}
                                    <Box component="span" fontWeight="fontWeightBold">
                                        Royal Bank of Canada
                                    </Box>{' '}
                                    and{' '}
                                    <Box component="span" fontWeight="fontWeightBold">
                                        LSoft Technologies
                                    </Box>
                                </Typography>
                            </Box>
                            {info && (
                                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                                    <Typography variant="subtitle2" fontSize="80%" sx={{ ml: 1 }}>
                                        You will be charged CAD {numeral(info.total * info.exRate).format('0,0.00')} at
                                        rate {info.exRate}
                                    </Typography>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </>
    );
};
